/**
 * A bridge to the router for code that lives outside the React tree.
 *
 * `lib/queryClient.js` needs to redirect on some API responses, but it is a
 * plain module — it has no access to `useNavigate()`. It used to reach for
 * `window.location.assign`, which triggers a full page reload: the SPA boots
 * from scratch, React Query's cache is dropped, and any half-typed clinical
 * note or transcript edit in the current view is lost.
 *
 * `<NavigationBridge />` (rendered inside `<BrowserRouter>` in App.jsx)
 * registers the router's navigate here on mount, so those redirects become
 * ordinary client-side transitions.
 */

let navigator = null

/** Called by NavigationBridge. Pass null to unregister on unmount. */
export function setNavigator(fn) {
  navigator = fn
}

/**
 * Normalise `path` to a same-origin, in-app path, or return null if it isn't
 * one.
 *
 * Callers pass values that originate from API responses (the 428
 * ai_key_required payload carries a `settings_url`), so a hostile or
 * compromised backend could otherwise redirect a logged-in clinician off-site.
 * Anything protocol-relative ("//evil"), backslash-prefixed ("\\evil" — the
 * form behind GHSA-wrjc-x8rr-h8h6), or carrying a scheme is rejected.
 *
 * Backslashes are folded to forward slashes *before* the check and the folded
 * value is what gets navigated, so the string we validate is the string the
 * browser resolves — validating one form and navigating another is exactly how
 * these bypasses work.
 *
 * @param {unknown} path
 * @returns {string|null}
 */
function toInternalPath(path) {
  if (typeof path !== 'string' || !path) return null
  const normalised = path.replace(/\\/g, '/')
  if (!normalised.startsWith('/')) return null
  if (normalised.startsWith('//')) return null
  return normalised
}

/**
 * Navigate without a page reload when the router is available, falling back
 * to a hard assign if it isn't (e.g. an error fires before the tree mounts).
 *
 * Off-site or malformed targets are ignored rather than followed.
 *
 * @param {string} path in-app path beginning with a single "/"
 * @param {{ replace?: boolean }} [options]
 * @returns {boolean} whether the navigation was performed
 */
export function navigateTo(path, options = {}) {
  const target = toInternalPath(path)
  if (!target) return false
  if (navigator) {
    navigator(target, { replace: !!options.replace })
    return true
  }
  window.location.assign(target)
  return true
}

/** Current pathname, guarded for non-browser contexts. */
export function currentPath() {
  return typeof window !== 'undefined' ? window.location.pathname : ''
}
